import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  LineChart, Line,
  XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { adminService } from '../../services/admin.service';
import { useTheme } from '../../contexts/ThemeContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const RANGE_OPTIONS = [
  { label: '7d',  value: 7  },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];

const MAX_SELECTED = 6;

// ─── Theme-aware chart palette ────────────────────────────────────────────────

function useChartPalette() {
  const { theme } = useTheme();
  return useMemo(() => {
    const s = getComputedStyle(document.documentElement);
    const rgb = (v: string) => s.getPropertyValue(v).trim();
    return {
      primary: `rgb(${rgb('--color-primary')})`,
      secondary: `rgb(${rgb('--color-secondary')})`,
      success: `rgb(${rgb('--color-success')})`,
      caution: `rgb(${rgb('--color-caution')})`,
      primaryDim: `rgb(${rgb('--color-primary-fixed-dim')})`,
      outline: `rgb(${rgb('--color-outline')})`,
      label: `rgb(${rgb('--color-on-surface-variant')})`,
      grid: `rgba(${rgb('--color-outline-variant')}, 0.15)`,
    };
  }, [theme]);
}

function getStudentColor(index: number, palette: ReturnType<typeof useChartPalette>) {
  const colors = [
    palette.primary,
    palette.success,
    palette.secondary,
    palette.caution,
    palette.primaryDim,
    palette.outline,
  ];
  return colors[index % colors.length];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function cssVar(name: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// ─── Vibrant stat card ────────────────────────────────────────────────────────

function StatCard({ label, value, icon, tone, sub }: {
  label: string; value: string | number; icon: string;
  tone: 'primary' | 'success' | 'caution' | 'neutral';
  sub?: string;
}) {
  const toneMap = {
    primary:  { icon: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/30', glow: 'shadow-primary/5' },
    success:  { icon: 'text-success', bg: 'bg-success/15', border: 'border-success/30', glow: 'shadow-success/5' },
    caution:  { icon: 'text-caution', bg: 'bg-caution/15', border: 'border-caution/30', glow: 'shadow-caution/5' },
    neutral:  { icon: 'text-on-surface-variant', bg: 'bg-surface-container', border: 'border-outline-variant/20', glow: 'shadow-none' },
  };
  const t = toneMap[tone];
  return (
    <div className={`relative overflow-hidden rounded-2xl border ${t.border} bg-surface-container-low p-6 shadow-sm ${t.glow} transition-all hover:-translate-y-0.5 hover:shadow-md`}>
      <div className={`absolute top-0 left-0 right-0 h-1 ${t.bg.replace('/10','').replace('/15','')}`} />
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[11px] text-on-surface-variant font-semibold uppercase tracking-wider mb-1">{label}</p>
          <p className="text-3xl font-extrabold text-on-surface tracking-tight">{value}</p>
          {sub && <p className="text-[11px] text-on-surface-variant mt-1.5 font-medium">{sub}</p>}
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${t.bg} shadow-sm`}>
          <span className={`material-symbols-outlined block leading-none ${t.icon} text-2xl`}>{icon}</span>
        </div>
      </div>
    </div>
  );
}

// ─── Score ring (mini radial progress) ───────────────────────────────────────

function MiniRing({ value, max = 100, size = 44, stroke = 4, color }: {
  value: number; max?: number; size?: number; stroke?: number; color: string;
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const filled = c * (value / max);
  return (
    <svg width={size} height={size} className="shrink-0 -rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke}
        className="text-outline-variant/20" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={`${filled} ${c}`} strokeLinecap="round"
        className="transition-all duration-700" />
    </svg>
  );
}

// ─── Engagement score card ────────────────────────────────────────────────────

function ScoreCard({
  label, value, max = 100, icon, tone, sub,
}: {
  label: string; value: number | null; max?: number; icon: string;
  tone: 'success' | 'primary' | 'caution';
  sub?: string;
}) {
  const toneMap = {
    success: { text: 'text-success', bg: 'bg-success/10', border: 'border-success/25', ring: '#20a73c' },
    primary: { text: 'text-primary', bg: 'bg-primary/10', border: 'border-primary/25', ring: '#4143c8' },
    caution: { text: 'text-caution', bg: 'bg-caution/10', border: 'border-caution/25', ring: '#c25f0a' },
  };
  const t = toneMap[tone];
  const displayValue = value ?? 0;
  const hasValue = value != null;

  return (
    <div className={`relative overflow-hidden rounded-2xl border ${t.border} bg-surface-container-low p-5 transition-all hover:-translate-y-0.5 hover:shadow-md`}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl ${t.bg} flex items-center justify-center border ${t.border}`}>
          <span className={`material-symbols-outlined ${t.text} text-2xl`}>{icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-on-surface-variant uppercase tracking-wider">{label}</p>
          <div className="flex items-baseline gap-1.5 mt-0.5">
            <span className={`text-3xl font-extrabold tracking-tight ${hasValue ? t.text : 'text-on-surface-variant/40'}`}>
              {hasValue ? displayValue : '—'}
            </span>
            {hasValue && <span className="text-sm text-on-surface-variant font-medium">/{max}</span>}
          </div>
          {sub && <p className="text-[11px] text-on-surface-variant mt-1 font-medium">{sub}</p>}
        </div>
        {hasValue && (
          <MiniRing value={displayValue} max={max} color={t.ring} />
        )}
      </div>
      {/* Mini segmented bar */}
      {hasValue && (
        <div className="mt-4 h-1.5 rounded-full bg-surface-container-high overflow-hidden ring-1 ring-inset ring-outline-variant/10">
          <div
            className={`h-full rounded-full ${tone === 'success' ? 'bg-success' : tone === 'primary' ? 'bg-primary' : 'bg-caution'} transition-all duration-700`}
            style={{ width: `${(displayValue / max) * 100}%` }}
          />
        </div>
      )}
    </div>
  );
}

// ─── Subscription mix bar ─────────────────────────────────────────────────────

function SubscriptionMix({ breakdown }: { breakdown: Array<{ status: string | null; count: number }> }) {
  const valid = breakdown.filter((b) => b.status != null);
  const total = valid.reduce((s, b) => s + b.count, 0);
  const colors: Record<string, string> = {
    ACTIVE:   'bg-success',
    TRIALING: 'bg-primary',
    PAST_DUE: 'bg-caution',
    CANCELED: 'bg-error',
  };
  const labels: Record<string, string> = {
    ACTIVE:   'Active',
    TRIALING: 'Trial',
    PAST_DUE: 'Past Due',
    CANCELED: 'Canceled',
  };
  const icons: Record<string, string> = {
    ACTIVE:   'check_circle',
    TRIALING: 'timer',
    PAST_DUE: 'payment',
    CANCELED: 'cancel',
  };

  return (
    <div className="relative overflow-hidden rounded-2xl border border-outline-variant/20 bg-surface-container-low p-5 transition-all hover:-translate-y-0.5 hover:shadow-md">
      <p className="text-[11px] font-semibold text-on-surface-variant uppercase tracking-wider mb-3">Subscription Mix</p>
      {/* Stacked bar */}
      <div className="flex h-3 rounded-full overflow-hidden bg-surface-container-high ring-1 ring-inset ring-outline-variant/10">
        {valid.map((b) => (
          <div
            key={b.status}
            className={`h-full ${colors[b.status!] ?? 'bg-surface-container'} transition-all duration-700`}
            style={{ width: `${total > 0 ? (b.count / total) * 100 : 0}%` }}
            title={`${labels[b.status!] ?? b.status}: ${b.count}`}
          />
        ))}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-3">
        {valid.map((b) => (
          <div key={b.status} className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${colors[b.status!] ?? 'bg-surface-container'}`} />
            <span className="material-symbols-outlined text-[13px] text-on-surface-variant">{icons[b.status!] ?? 'circle'}</span>
            <span className="text-[11px] text-on-surface-variant font-medium">{labels[b.status!] ?? b.status}</span>
            <span className="text-[11px] font-bold text-on-surface">{b.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Chart tooltip ────────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const visible = payload.filter(p => p.value > 0);
  if (!visible.length) return null;
  const total = visible.reduce((s, p) => s + p.value, 0);
  return (
    <div className="bg-surface-container-highest border border-outline-variant/30 rounded-lg px-3 py-2.5 text-xs shadow-lg min-w-[130px]">
      <p className="text-on-surface-variant mb-2 font-medium">
        {new Date((label ?? '') + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' })}
      </p>
      {visible.map(p => (
        <div key={p.name} className="flex items-center justify-between gap-4 mb-0.5">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
            <span className="text-on-surface-variant capitalize">{p.name}</span>
          </div>
          <span className="font-semibold text-on-surface">{p.value}</span>
        </div>
      ))}
      {visible.length > 1 && (
        <div className="flex justify-between mt-1.5 pt-1.5 border-t border-outline-variant/20">
          <span className="text-on-surface-variant">Total</span>
          <span className="font-bold text-on-surface">{total}</span>
        </div>
      )}
    </div>
  );
}

function Avatar({ name, email, color }: { name: string | null; email: string; color?: string }) {
  const letter = (name ?? email)[0].toUpperCase();
  return (
    <div
      className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 text-white select-none"
      style={{ background: color ?? `rgb(${cssVar('--color-outline')})` }}
    >
      {letter}
    </div>
  );
}

// ─── Mentor insight cards ─────────────────────────────────────────────────────

function MentorInsightCard({
  tone,
  icon,
  title,
  description,
  actionLabel,
  onAction,
}: {
  tone: 'caution' | 'error' | 'success' | 'primary';
  icon: string;
  title: string;
  description: string;
  actionLabel: string;
  onAction: () => void;
}) {
  const toneStyles = {
    caution:  'border-l-caution bg-caution/5',
    error:    'border-l-error bg-error/5',
    success:  'border-l-success bg-success/5',
    primary:  'border-l-primary bg-primary/5',
  };
  const iconStyles = {
    caution:  'text-caution',
    error:    'text-error',
    success:  'text-success',
    primary:  'text-primary',
  };

  return (
    <div className={`rounded-xl border-l-4 p-5 ${toneStyles[tone]}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className={`material-symbols-outlined ${iconStyles[tone]} text-[22px] mt-0.5`}>{icon}</span>
          <div>
            <p className="text-sm font-semibold text-on-surface">{title}</p>
            <p className="text-xs text-on-surface-variant mt-1 leading-relaxed">{description}</p>
          </div>
        </div>
        <button
          onClick={onAction}
          className="shrink-0 text-xs font-semibold text-primary hover:text-on-primary-container hover:bg-primary/10 px-3 py-1.5 rounded-lg transition-colors"
        >
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

// ─── Activity Monitor ─────────────────────────────────────────────────────────

function PlatformActivityMonitor() {
  const palette = useChartPalette();
  const [days, setDays]             = useState(30);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch]         = useState('');

  const {
    data: studentsData,
    isLoading: loadingStudents,
    isError:   errorStudents,
  } = useQuery({
    queryKey: ['admin-students-activity', days],
    queryFn:  () => adminService.getStudentsActivity(days),
  });

  const selectionOrder = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const colorFor = (id: string) => {
    const i = selectionOrder.indexOf(id);
    return i >= 0 ? getStudentColor(i, palette) : undefined;
  };

  const visibleStudents = useMemo(() => {
    const all = studentsData ?? [];
    return selectedIds.size === 0 ? all : all.filter(s => selectedIds.has(s.id));
  }, [studentsData, selectedIds]);

  const chartData = useMemo(() => {
    if (visibleStudents.length === 0) return [];
    const dates: string[] = [];
    for (let i = days - 1; i >= 0; i--) {
      dates.push(new Date(Date.now() - i * 86_400_000).toISOString().slice(0, 10));
    }
    return dates.map(date => {
      const row: Record<string, string | number> = { date };
      for (const s of visibleStudents) {
        row[s.id] = s.dailySeries.find(d => d.date === date)?.total ?? 0;
      }
      return row;
    });
  }, [visibleStudents, days]);

  const tickInterval = days === 7 ? 0 : days === 30 ? 4 : 13;

  const filteredStudents = useMemo(() => {
    if (!studentsData) return [];
    const q = search.toLowerCase();
    return studentsData.filter(s =>
      !q || (s.name ?? '').toLowerCase().includes(q) || s.email.toLowerCase().includes(q),
    );
  }, [studentsData, search]);

  const toggleStudent = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_SELECTED) next.add(id);
      return next;
    });
  };

  const changeRange = (d: number) => {
    setDays(d);
    setSelectedIds(new Set());
  };

  const totalEvents  = chartData.reduce((acc, row) => {
    const { date: _d, ...rest } = row as Record<string, unknown>;
    return acc + Object.values(rest).reduce((s: number, v) => s + Number(v), 0);
  }, 0);
  const activeDays = chartData.filter(row => {
    const { date: _d, ...rest } = row as Record<string, unknown>;
    return Object.values(rest).some(v => Number(v) > 0);
  }).length;

  return (
    <section className="bg-surface-container-low rounded-xl overflow-hidden border border-outline-variant/10">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/10">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary text-[18px]">monitor_heart</span>
          <h3 className="text-xs font-semibold text-on-surface uppercase tracking-widest">
            Activity Monitor
          </h3>
          {selectedIds.size > 0 && (
            <span className="text-[11px] text-on-surface-variant">
              — {selectedIds.size} student{selectedIds.size > 1 ? 's' : ''} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {selectedIds.size > 0 && (
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-[11px] text-on-surface-variant hover:text-on-surface flex items-center gap-1 transition-colors"
            >
              <span className="material-symbols-outlined text-[13px]">close</span>
              Clear
            </button>
          )}
          <div className="flex gap-1">
            {RANGE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => changeRange(opt.value)}
                className={`px-2.5 py-1 rounded text-xs font-semibold transition-colors ${
                  days === opt.value
                    ? 'bg-primary text-on-primary'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body: chart + student panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px]">

        {/* Chart column */}
        <div className="p-6 border-b lg:border-b-0 lg:border-r border-outline-variant/10">
          {/* Stats row */}
          <div className="flex gap-6 mb-5">
            <div>
              <p className="text-lg font-bold text-on-surface">{totalEvents}</p>
              <p className="text-[11px] text-on-surface-variant">Total events</p>
            </div>
            <div>
              <p className="text-lg font-bold text-on-surface">{activeDays}</p>
              <p className="text-[11px] text-on-surface-variant">Active days</p>
            </div>
            {activeDays > 0 && (
              <div>
                <p className="text-lg font-bold text-on-surface">
                  {Math.round((totalEvents / activeDays) * 10) / 10}
                </p>
                <p className="text-[11px] text-on-surface-variant">Avg / active day</p>
              </div>
            )}
          </div>

          {/* Chart */}
          {loadingStudents ? (
            <div className="h-52 rounded-lg bg-surface-container animate-pulse" />
          ) : errorStudents ? (
            <div className="h-52 flex flex-col items-center justify-center gap-2">
              <span className="material-symbols-outlined text-error text-3xl">error</span>
              <p className="text-sm text-on-surface-variant">Could not load activity data.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart
                data={chartData}
                margin={{ top: 4, right: 8, left: -20, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={palette.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  interval={tickInterval}
                  tick={{ fontSize: 10, fill: palette.label }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: string) =>
                    new Date(v + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' })
                  }
                />
                <YAxis
                  tick={{ fontSize: 10, fill: palette.label }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: palette.grid, strokeWidth: 1 }} />
                <Legend
                  iconType="circle"
                  iconSize={7}
                  wrapperStyle={{ fontSize: 11, paddingTop: 14, color: palette.label }}
                />

                {visibleStudents.map((s, i) => (
                  <Line
                    key={s.id}
                    type="monotone"
                    dataKey={s.id}
                    name={s.name ?? s.email.split('@')[0]}
                    stroke={getStudentColor(i, palette)}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Student panel */}
        <div className="flex flex-col min-h-0">
          {/* Search */}
          <div className="px-4 pt-4 pb-3 border-b border-outline-variant/10">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant text-[15px]">
                search
              </span>
              <input
                type="text"
                placeholder="Filter students…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="input-field pl-8 w-full text-xs py-1.5"
              />
            </div>
            <p className="text-[10px] text-on-surface-variant mt-2">
              {selectedIds.size > 0
                ? `${selectedIds.size}/${MAX_SELECTED} selected — click to toggle`
                : 'Click a student to compare'}
            </p>
          </div>

          {/* Student list */}
          <div className="overflow-y-auto" style={{ maxHeight: 310 }}>
            {loadingStudents ? (
              <div className="p-3 space-y-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-11 bg-surface-container rounded-lg animate-pulse" />
                ))}
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-xs text-on-surface-variant">No students found.</p>
              </div>
            ) : (
              filteredStudents.map(student => {
                const isSelected = selectedIds.has(student.id);
                const color      = colorFor(student.id);
                const maxTotal   = filteredStudents[0]?.total ?? 1;
                const pct        = maxTotal > 0 ? (student.total / maxTotal) * 100 : 0;
                const disabled   = !isSelected && selectedIds.size >= MAX_SELECTED;
                return (
                  <button
                    key={student.id}
                    onClick={() => toggleStudent(student.id)}
                    disabled={disabled}
                    className={[
                      'w-full flex items-center gap-3 px-4 py-3 text-left',
                      'border-b border-outline-variant/10 last:border-0 transition-colors',
                      isSelected ? 'bg-surface-container' : 'hover:bg-surface-container/60',
                      disabled ? 'opacity-40 cursor-not-allowed' : '',
                    ].join(' ')}
                  >
                    <Avatar name={student.name} email={student.email} color={isSelected ? color : undefined} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p className="text-xs font-medium text-on-surface truncate">
                          {student.name ?? student.email.split('@')[0]}
                        </p>
                        <span className={`text-[10px] font-bold shrink-0 tabular-nums ${isSelected ? 'text-on-surface' : 'text-on-surface-variant'}`}>
                          {student.total}
                        </span>
                      </div>
                      <div className="w-full h-1 rounded-full overflow-hidden mt-1.5" style={{ background: palette.grid }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${pct}%`, background: isSelected ? color : `rgb(${cssVar('--color-outline-variant')})` }}
                        />
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminOverview() {
  const navigate = useNavigate();

  const { data: overview, isLoading } = useQuery({
    queryKey: ['admin-overview'],
    queryFn:  () => adminService.getOverview(),
  });

  if (isLoading || !overview) {
    return (
      <div className="p-12 max-w-7xl mx-auto space-y-6">
        <div className="h-8 w-48 bg-surface-container-high rounded animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-28 bg-surface-container-high rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const inactiveStudents = Math.max(
    0,
    overview.totalStudents - overview.activeStudents - overview.atRiskStudents,
  );

  return (
    <div className="p-4 md:p-8 lg:p-12 max-w-7xl mx-auto space-y-8 md:space-y-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="space-y-1">
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary font-bold">Mentor Dashboard</p>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-on-surface">Platform Overview</h2>
          <p className="text-on-surface-variant">A focused view on what needs your mentoring attention.</p>
        </div>
        <div className="flex gap-3 shrink-0">
          <button
            onClick={() => navigate('/admin/students')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl primary-gradient text-on-primary text-sm font-bold shadow-lg shadow-primary/15 hover:shadow-primary/25 hover:-translate-y-0.5 transition-all"
          >
            <span className="material-symbols-outlined text-lg">group</span>
            All Students
          </button>
          <button
            onClick={() => navigate('/admin/students?status=at-risk')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-caution/10 text-caution border border-caution/20 font-bold text-sm hover:bg-caution/20 transition-all hover:-translate-y-0.5"
          >
            <span className="material-symbols-outlined text-lg">person_alert</span>
            At-Risk
          </button>
        </div>
      </div>

      {/* Mentor insights — actionable cards */}
      {overview.atRiskStudents > 0 && (
        <section className="space-y-3">
          <h3 className="text-xs uppercase tracking-[0.15em] text-on-surface-variant font-semibold">
            Attention Needed
          </h3>
          <MentorInsightCard
            tone="caution"
            icon="person_alert"
            title={`${overview.atRiskStudents} student${overview.atRiskStudents !== 1 ? 's' : ''} at risk`}
            description="These students have been inactive for 14+ days and have less than 50% training progress. Consider reaching out to re-engage them."
            actionLabel="View at-risk"
            onAction={() => navigate('/admin/students?status=at-risk')}
          />
        </section>
      )}

      {/* Student status KPIs */}
      <section className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.15em] text-on-surface-variant font-semibold">
          Student Status
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Total Students"  value={overview.totalStudents}  icon="group"        tone="neutral" />
          <StatCard label="Active"           value={overview.activeStudents} icon="check_circle" tone="success"  sub="Activity in last 30 days" />
          <StatCard label="At Risk"          value={overview.atRiskStudents} icon="warning"      tone="caution"  sub="Inactive 14+ days & progress < 50%" />
        </div>
      </section>

      {/* Engagement Snapshot — performance & quality metrics */}
      <section className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.15em] text-on-surface-variant font-semibold">
          Engagement Snapshot
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <ScoreCard
            label="Training Velocity"
            value={Math.round(overview.avgTrainingProgress)}
            icon="school"
            tone={overview.avgTrainingProgress >= 75 ? 'success' : overview.avgTrainingProgress >= 40 ? 'primary' : 'caution'}
            sub="Average progress across cohort"
          />
          <ScoreCard
            label="Interview Readiness"
            value={overview.avgInterviewScore != null ? Math.round(overview.avgInterviewScore) : null}
            max={100}
            icon="mic"
            tone={(overview.avgInterviewScore ?? 0) >= 70 ? 'success' : (overview.avgInterviewScore ?? 0) >= 50 ? 'primary' : 'caution'}
            sub="Average mock-interview score"
          />
          <ScoreCard
            label="CV Health"
            value={overview.avgCvScore != null ? Math.round(overview.avgCvScore) : null}
            max={100}
            icon="description"
            tone={(overview.avgCvScore ?? 0) >= 70 ? 'success' : (overview.avgCvScore ?? 0) >= 50 ? 'primary' : 'caution'}
            sub="Average resume quality score"
          />
          <SubscriptionMix breakdown={overview.subscriptionBreakdown} />
        </div>
      </section>

      {/* Activity Monitor */}
      <PlatformActivityMonitor />
    </div>
  );
}
